// react
import { Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import { CommentPayload, ErrorCause, Feedback } from '../../../@types';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// props
export interface FormAddCommentProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddComment = ({ onClose, onSuccess, setIsLoading }: FormAddCommentProps) => {
	// hooks
	const { idAccount } = useParams();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogAccount = Number(searchParams.get('dialogAccount')) || undefined;
	const paramDialogContract = Number(searchParams.get('dialogContract')) || undefined;
	const paramDialogTransaction = Number(searchParams.get('dialogTransaction')) || undefined;

	// context
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);

	// vars
	const idForm = 'form-add-comment';

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// set loader
			setIsLoading(true);

			const body: CommentPayload = {
				comment: entries.comments as string,
			};

			if (idAccount || paramDialogAccount) {
				body.account = Number(idAccount || paramDialogAccount);
			} else if (paramDialogContract) {
				body.contract = paramDialogContract;
			} else if (paramDialogTransaction) {
				body.transaction = paramDialogTransaction;
			}

			if (user?.id) {
				body.user = user.id;
			}

			const fetchAddComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddComment = await fetchAddComment.json();

			if (resAddComment.error) {
				throw new Error(resAddComment.error.message, {
					cause: {
						id: resAddComment.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.commentCreated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'COMMENT_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} name="formAddComment" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-comments`}
						label={t('comments')}
						minRows={4}
						multiline={true}
						name="comments"
						required={true}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
